import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { feedMochi } from "@/lib/api";
import { getMochiMeter } from "@/lib/mochiMeter";
import { MochiMeterPill } from "@/components/MochiMeter";

// Small inline SVG of a rice ball (onigiri): cream triangle body, black nori
// strip, two tiny eye dots. ~32×32 by default.
function RiceBallIcon({ size = 32 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
            {/* Soft drop shadow under the rice ball */}
            <ellipse cx="16" cy="28" rx="9" ry="1.6" fill="rgba(27,27,27,0.18)" />
            {/* Rice triangle body */}
            <path
                d="M16 5
                   C19 5 22 8 25 17
                   C26 21 23 25 16 25
                   C9 25 6 21 7 17
                   C10 8 13 5 16 5 Z"
                fill="#FDFBF7"
                stroke="#1B1B1B"
                strokeWidth="1.6"
                strokeLinejoin="round"
            />
            {/* Nori (seaweed) strip at the bottom */}
            <path
                d="M9 19
                   Q16 23 23 19
                   L22 24
                   Q16 26 10 24 Z"
                fill="#2A2A2A"
                stroke="#1B1B1B"
                strokeWidth="1"
            />
            {/* Tiny kawaii dot eyes */}
            <circle cx="13" cy="16" r="1" fill="#1B1B1B" />
            <circle cx="19" cy="16" r="1" fill="#1B1B1B" />
        </svg>
    );
}

// `targetRef` should point at the avatar container so the flying rice ball
// knows where to fly to. `onFedLocal` lets the parent trigger a transient
// "nom" reaction on Mochi (e.g. swap mood to "excited" for 0.8s).
export default function RiceBallButton({ settings, onSettingsUpdate, targetRef, onFedLocal, onLevelChange, onIgnite }) {
    const [flying, setFlying] = useState([]);
    const [busy, setBusy] = useState(false);
    const [levelUpKey, setLevelUpKey] = useState(0);

    if (!settings) return null;
    const jar = settings.rice_balls_in_jar || 0;
    const fed = settings.rice_balls_fed_total || 0;
    const meterVal = settings.meter_value ?? 0;
    const canFeed = jar > 0;

    const handleFeed = async (e) => {
        if (!canFeed || busy) return;
        setBusy(true);

        // Snapshot meter BEFORE feeding so we can detect a level-up
        const before = getMochiMeter(meterVal);

        // Compute flight path from the button to the avatar center
        const btnRect = e.currentTarget.getBoundingClientRect();
        const tgtRect = targetRef?.current?.getBoundingClientRect();
        const startX = btnRect.left + btnRect.width / 2;
        const startY = btnRect.top + btnRect.height / 2;
        const endX = tgtRect ? tgtRect.left + tgtRect.width / 2 : startX;
        const endY = tgtRect ? tgtRect.top + tgtRect.height * 0.55 : startY - 200;
        const flightId = `${Date.now()}-${Math.random()}`;
        setFlying((f) => [...f, { id: flightId, startX, startY, endX, endY }]);
        setTimeout(() => setFlying((f) => f.filter((x) => x.id !== flightId)), 800);
        onFedLocal?.();

        try {
            const res = await feedMochi(1);
            // Optimistically reflect new jar/fed/meter counts via parent
            onSettingsUpdate?.({
                rice_balls_in_jar: res.jar,
                rice_balls_fed_total: res.fed_total,
                meter_value: res.meter_value,
                meter_last_update_at: res.meter_last_update_at,
            });
            // Detect a meter level-up (Spark→Flame, etc.) and celebrate.
            // Reaching Inferno (level 5) ALSO triggers the fire-dance overlay.
            const after = getMochiMeter(res.meter_value);
            if (after.current.level > before.current.level) {
                setLevelUpKey((k) => k + 1);
                setTimeout(
                    () => toast(`🔥 Mochi reached ${after.current.name}!`, { duration: 5000 }),
                    700,
                );
                onLevelChange?.({ from: before.current.level, to: after.current.level, name: after.current.name });
            }
            if (res.milestone) {
                setTimeout(
                    () => toast(`🍡 ${res.milestone}`, { duration: 5000 }),
                    800,
                );
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || "Jar's empty 🍡";
            toast(msg, { duration: 3500 });
        } finally {
            setTimeout(() => setBusy(false), 350);
        }
    };

    const label = jar;

    return (
        <>
            <div className="flex items-center gap-2">
                <motion.button
                    type="button"
                    onClick={handleFeed}
                    disabled={!canFeed}
                    whileHover={canFeed ? { scale: 1.06 } : undefined}
                    whileTap={canFeed ? { scale: 0.9, rotate: -8 } : undefined}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-full font-display text-sm shadow-clay transition-all border-2 ${
                        canFeed
                            ? "bg-mustard text-warm-900 border-mustard hover:shadow-float"
                            : "bg-surface text-warm-500 border-transparent opacity-60 cursor-not-allowed"
                    }`}
                    data-testid="riceball-feed-btn"
                    aria-label={canFeed ? "Feed Mochi a rice ball" : "Jar is empty"}
                >
                    <RiceBallIcon size={22} />
                    <span className="font-display text-sm leading-none" data-testid="riceball-jar-count">
                        Feed · {label}
                    </span>
                </motion.button>
                <MochiMeterPill key={levelUpKey} meterValue={meterVal} animateLevelUp={levelUpKey > 0} onIgnite={onIgnite} />
            </div>

            {/* Flying rice ball — rendered in a fixed-position portal-style overlay */}
            <AnimatePresence>
                {flying.map((f) => (
                    <motion.div
                        key={f.id}
                        initial={{ x: f.startX, y: f.startY, scale: 1, opacity: 1, rotate: 0 }}
                        animate={{
                            x: [f.startX, (f.startX + f.endX) / 2, f.endX],
                            y: [f.startY, Math.min(f.startY, f.endY) - 80, f.endY],
                            scale: [1, 1.15, 0.65],
                            rotate: [0, 360],
                            opacity: [1, 1, 0],
                        }}
                        transition={{ duration: 0.7, ease: "easeIn", times: [0, 0.5, 1] }}
                        className="pointer-events-none fixed top-0 left-0 z-50"
                        style={{ x: f.startX, y: f.startY, translate: "-50% -50%" }}
                        data-testid="riceball-flying"
                    >
                        <RiceBallIcon size={28} />
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* Caption under the button when at-zero */}
            {jar === 0 && fed > 0 && (
                <p
                    className="font-body text-[10px] text-warm-500 mt-1 text-center"
                    data-testid="riceball-empty-hint"
                >
                    Finish a task to fill the jar 🍡
                </p>
            )}
        </>
    );
}
